'use client';

import { useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'cleanor-online-notes-v1';

function countWords(text: string) {
  const trimmed = text.trim();
  if (!trimmed) {
    return 0;
  }
  return trimmed.split(/\s+/).length;
}

export default function BrowserOnlineNotes() {
  const [text, setText] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const skipFirstSave = useRef(true);

  // Load saved note after mount so server and first client render match.
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    let cancelled = false;
    async function load() {
      // Defer past the synchronous render so seeding never blocks hydration.
      await Promise.resolve();
      if (cancelled) {
        return;
      }
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (typeof raw === 'string') {
          setText(raw);
        }
      } catch {
        // Ignore storage read errors.
      }
      setHydrated(true);
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Autosave after hydration, skipping the initial run.
  useEffect(() => {
    if (!hydrated || typeof window === 'undefined') {
      return;
    }
    if (skipFirstSave.current) {
      skipFirstSave.current = false;
      return;
    }
    const timer = window.setTimeout(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, text);
        setSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
      } catch {
        // Ignore quota errors.
      }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [text, hydrated]);

  function clearNote() {
    setText('');
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Ignore storage errors.
      }
    }
    setSavedAt(null);
  }

  const characters = text.length;
  const words = countWords(text);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Online Notes</span>
        <h2 className="tool-live-title">Online notepad</h2>
        <p className="tool-live-subtitle">
          A distraction-free notepad that autosaves as you type. Your note stays in this browser,
          survives a reload, and never gets uploaded anywhere.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <label className="tool-live-field tool-live-field-full">
            <span>Your note</span>
            <textarea
              onChange={(event) => setText(event.target.value)}
              placeholder="Start typing… your note saves automatically."
              rows={14}
              value={text}
            />
          </label>
          <div className="tool-live-actions">
            <button
              className="button button-secondary"
              disabled={text.length === 0}
              onClick={clearNote}
              type="button"
            >
              Clear note
            </button>
          </div>
          {savedAt ? <p className="status-message status-success">Saved at {savedAt}.</p> : null}
        </section>

        <section className="tool-live-preview">
          <div className="tool-live-field">
            <span>Words</span>
            <input readOnly type="text" value={String(words)} />
          </div>
          <div className="tool-live-field">
            <span>Characters</span>
            <input readOnly type="text" value={String(characters)} />
          </div>
          <p className="tool-live-panel-note">
            Notes are stored locally in your browser. Clearing your browser data or using private
            mode will remove them.
          </p>
        </section>
      </div>
    </div>
  );
}
